import { useRef, useCallback } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Bold, Italic } from 'lucide-react';

interface RichTextareaProps {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
}

export default function RichTextarea({ value, onChange, rows = 3, placeholder }: RichTextareaProps) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const wrapSelection = useCallback((wrapper: '**' | '*') => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.slice(start, end);
    if (!selected) return;

    // Check if already wrapped
    const wLen = wrapper.length;
    const before = value.slice(start - wLen, start);
    const after = value.slice(end, end + wLen);
    if (before === wrapper && after === wrapper) {
      // Unwrap
      const newValue = value.slice(0, start - wLen) + selected + value.slice(end + wLen);
      onChange(newValue);
      setTimeout(() => { el.selectionStart = start - wLen; el.selectionEnd = end - wLen; }, 0);
    } else {
      const newValue = value.slice(0, start) + wrapper + selected + wrapper + value.slice(end);
      onChange(newValue);
      setTimeout(() => { el.selectionStart = start + wLen; el.selectionEnd = end + wLen; }, 0);
    }
    el.focus();
  }, [value, onChange]);

  return (
    <div>
      <div className="flex gap-1 mb-1">
        <Button type="button" variant="outline" size="sm" className="h-7 px-2" onClick={() => wrapSelection('**')} title="Bold">
          <Bold className="h-3.5 w-3.5" />
        </Button>
        <Button type="button" variant="outline" size="sm" className="h-7 px-2" onClick={() => wrapSelection('*')} title="Italic">
          <Italic className="h-3.5 w-3.5" />
        </Button>
      </div>
      <Textarea
        ref={ref}
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
      />
    </div>
  );
}
